import { mapState, mapGetters } from 'vuex'
import isEqual from 'lodash/isEqual'
import { formatDateZone } from '@utils/date-helpers'

export default {
  computed: {
    ...mapState('modifyReservation', ['oldReservationData']),
    ...mapGetters('modifyReservation', ['getBookingTrackingModifyReservation']),

    hotelCode() {
      return this.currentReservationData
        ? this.currentReservationData?.reservations[0].hotelCode
        : 'Global'
    },
    multiRoomTotalWithoutCurrency() {
      return Math.round(this.currentReservationsList
        .filter((item) => item.status === 'Committed')
        .map((item) => item.total.total)
        .reduce((sum, current) => sum + current, 0))
    },
    totalCommittedTaxes() {
      return Math.round(parseFloat(
        this.currentReservationsList
          .filter((item) => item.status === 'Committed')
          .flatMap((item) => item.total.taxesFees)
          .map((item) => item.price)
          .reduce((sum, current) => sum + current, 0)
          .toFixed(2)
      ))
    },
    totalTaxes() {
      const totalTaxes = parseFloat(
        this.currentReservationsList
          .flatMap((item) => item.total.taxesFees)
          .map((item) => item.price)
          .reduce((sum, current) => sum + current, 0)
          .toFixed(2)
      )
      return Math.round(totalTaxes)
    },
  },
  methods: {
    triggerViewResAdobeEvent() {
      if (
        this.showReservationModifiedSuccess
          || this.selectedReservationstoCancellChanged
      ) {
        return
      }
      this.$track(
        this.getBookingTrackingModifyReservation(
          {
            name: 'home >> modify >> find-reservation >> details',
            hotelCode: this.hotelCode,
            siteSection: 'find - details',
          },
          { event: 'viewRes' }
        )
      )
      this.$track(
        this.getBookingTrackingModifyReservation(
          undefined,
          {
            event: 'bookingStateChange',
            bookingEngine: {
              bookingStepNumber: '0.1',
              bookingStepName: 'View Reservation',
              bookingEngineVersion: '2',
              bookingFlow: this.isMultiRoomReservation ? 'multiRoomMod' : 'singleRoomMod'
            }
          }
        )
      )
    },
    // SBE-56 Adobe Tracking Cancellation Event
    adobeEventCancelReservations() {
      const currentDate = new Date()
      const selectedConfirmationNumbers = this.selectedReservationsToCancel.data.map(
        (reservation) => reservation.cancelInfoRS.uniqueID.id
      )

      const cancelledReservationsTotal = this.currentReservationsList
        .filter((reservation) => selectedConfirmationNumbers.includes(reservation.id))
        .map((item) => item.total.total)
        .reduce((sum, current) => sum + current, 0)

      const cancelledReservationsTaxes = this.currentReservationsList
        .filter((reservation) => selectedConfirmationNumbers.includes(reservation.id))
        .flatMap((item) => item.total.taxesFees)
        .map((item) => item.price)
        .reduce((sum, current) => sum + current, 0)
        .toFixed(2)

      let cancelTotal = this.currentReservationsList
        .map((item) => item.total.total)
        .reduce((sum, current) => sum + current, 0) - this.totalTaxes
      cancelTotal = Math.round(cancelTotal).toString()
      const cancelTotalTaxes = this.totalTaxes.toString()

      const commonDetails = {
        itineraryID: this.modifyReservationItineraryNumber,
        hotelCode: this.currentReservationsList[0]?.hotelCode || null,
        checkIn: this.startDate,
        checkOut: this.endDate,
        date: formatDateZone(currentDate, null, 'ddd, D MMM, YYYY'),
        roomQty: this.currentReservationsList.length.toString(),
        tax: cancelTotalTaxes,
        currency: this.currencyCode,
        cancellationRevLost: (
          cancelledReservationsTotal - cancelledReservationsTaxes
        ).toFixed(2),
        total: cancelTotal,
      }

      const rooms = this.currentReservationsList
        .filter((reservation) => selectedConfirmationNumbers.includes(reservation.id))
        .map((reservation) => ({
          confirmationID: reservation.reservationIds
            .flatMap((item) => item)
            .filter((item) => item.source === 'RedX' && item.type === '14')
            .map((item) => item.value)
            .join(''),
          type: reservation.roomStays[0].roomName,
          rate: reservation.ratePlanNameSynXis,
          numNights: reservation.prices.nightlyRates.length.toString(),
          numAdults: reservation.adultsCount.toString(),
          numChildren: reservation.childCount.toString(),
          daysToCheckIn: this.calculateDaysBetweenDates(this.startDate),
          price: reservation.roomStays[0].total.amountBeforeTax.toString(),
          tax: Math.round(
            reservation.total.taxesFees
              .map((item) => item.price)
              .reduce((sum, current) => sum + current, 0)
          ).toString(),
          enhancements: reservation.enhancementServices
            ? reservation.enhancementServices
              .map((item) => item.title)
              .join(' > ')
            : '',
        }))

      // Construct the final transformedReservations object
      const transformedReservations = {
        reservation: commonDetails,
        rooms,
      }

      const baseTaggingData = transformedReservations

      this.$track(
        this.getBookingTrackingModifyReservation(
          {
            name: 'home >> cancel-confirmation',
            hotelCode: this.hotelCode,
            siteSection: 'cancel confirmation',
          },
          {
            event: 'cancelRes',
            reservation: baseTaggingData.reservation,
            rooms: baseTaggingData.rooms,
          }
        )
      )

      this.$track(
        this.getBookingTrackingModifyReservation(undefined,
          {
            event: 'bookingStateChange',
            bookingEngine: {
              bookingStepNumber: '1',
              bookingStepName: 'Confirmation',
              bookingEngineVersion: '2',
              bookingFlow: this.isMultiRoomReservation ? 'multiRoomMod' : 'singleRoomMod'
            }
          })
      )
    },

    compareReservationsAfterModification() {
      if (!this.showReservationModifiedSuccess) {
        return null
      }

      const currentReservationsMap = new Map(
        this.currentReservationsList.map((res) => [res.id, res])
      )
      const oldReservationsMap = new Map(
        this.oldReservationData.reservations.map((res) => [res.id, res])
      )

      const changedRooms = this.oldReservationData.reservations.filter((oldRes) => {
        const currentRes = currentReservationsMap.get(oldRes.id)

        if (!currentRes) return false

        const roomStaysChanged = !isEqual(
          oldRes.roomStays,
          currentRes.roomStays
        )

        const ratePlanNameSynXisChanged = oldRes.ratePlanNameSynXis !== currentRes.ratePlanNameSynXis

        return roomStaysChanged || ratePlanNameSynXisChanged
      })

      const changedGuests = this.oldReservationData.reservations.filter((oldRes) => {
        const currentRes = currentReservationsMap.get(oldRes.id)

        if (!currentRes) return false

        const oldProfiles = oldRes?.guests ? oldRes.guests.map((g) => g.profiles) : []
        const currentProfiles = currentRes?.guests ? currentRes.guests.map((g) => g.profiles) : []

        return !isEqual(
          oldProfiles,
          currentProfiles
        )
      })

      const changedEnhancements = this.oldReservationData.reservations.filter((oldRes) => {
        const currentRes = currentReservationsMap.get(oldRes.id)

        if (!currentRes) return false

        if (
          oldRes.enhancementServices.length === 0
          && currentRes.enhancementServices.length === 0
        ) {
          return false
        }

        const oldEnhancements = oldRes.enhancementServices.map(
          ({ title, description }) => ({ title, description })
        )
        const currentEnhancements = currentRes.enhancementServices.map(
          ({ title, description }) => ({ title, description })
        )

        return !isEqual(oldEnhancements, currentEnhancements)
      })

      const currentDate = new Date()

      // Past Reservation vs New Reservation Revenue
      let modifiedTotal = this.multiRoomTotalWithoutCurrency - this.totalCommittedTaxes
      modifiedTotal = Math.round(modifiedTotal)

      let pastModifiedTotal = this.oldReservationData.reservations
        .filter((item) => item.status === 'Committed')
        .map((item) => item.total.total)
        .reduce((sum, current) => sum + current, 0)
      pastModifiedTotal = Math.round(pastModifiedTotal)

      const sumOfPastTaxes = this.oldReservationData.reservations
        .filter((item) => item.status === 'Committed')
        .flatMap((item) => item.total.taxesFees || [])
        .map((taxEntry) => taxEntry.price)
        .reduce((sum, current) => sum + current, 0)

      const pastModifiedTotalTaxes = Math.round(sumOfPastTaxes)

      const pastTotalBeforeTaxes = pastModifiedTotal - pastModifiedTotalTaxes

      // SBE-57 Adobe Tracking Modify Rooms & Hotels Event
      if (changedRooms.length > 0) {
        const reservationsModifyRoomsRootData = {
          itineraryID: this.modifyReservationItineraryNumber,
          hotelCode: this.currentReservationsList[0]?.hotelCode || null,
          checkIn: this.startDate,
          checkOut: this.endDate,
          date: formatDateZone(currentDate, null, 'ddd, D MMM, YYYY'),
          roomQty: this.currentReservationsList.length.toString(),
          tax: this.totalCommittedTaxes.toString(),
          currency: this.currencyCode,
          total: modifiedTotal.toString(),
          modificationRevChange: (
            modifiedTotal - pastTotalBeforeTaxes
          ).toString(),
          promoCode: this.currentReservationsList[0]?.roomStays[0]?.promotionCode || '',
        }

        const roomsModifyData = changedRooms.map(
          (changedReservation) => {
          // Find the matching reservation from currentReservationsMap
            const matchedReservation = currentReservationsMap.get(changedReservation.id)

            if (!matchedReservation) {
              return null
            }

            const changeRoomPrice = Math.round(
              matchedReservation.roomStays[0]?.total?.amountBeforeTax || 0
            )
            const changeRoomtax = Math.round(
              (matchedReservation.roomStays[0]?.total.amountAfterTax || 0)
              - (matchedReservation.roomStays[0]?.total?.amountBeforeTax || 0)
            )

            const oldMatchingReservation = oldReservationsMap.get(matchedReservation.id)
            const prevEnhancements = (oldMatchingReservation?.enhancementServices || [])
              .map((item) => item.title)
              .join(' > ')

            return {
              confirmationID: matchedReservation.id,
              type: matchedReservation.roomStays[0]?.roomName,
              rate: matchedReservation.ratePlanNameSynXis,
              numNights:
                  matchedReservation.prices.nightlyRates.length.toString(),
              numAdults: matchedReservation.adultsCount.toString(),
              numChildren: matchedReservation.childCount.toString(),
              daysToCheckIn: this.calculateDaysBetweenDates(this.startDate),
              price: changeRoomPrice.toString(),
              tax: changeRoomtax.toString(),
              enhancements: matchedReservation?.enhancementServices || []
                ? matchedReservation.enhancementServices
                  .map((item) => item.title)
                  .join(' > ')
                : '',
              prevEnhancements
            }
          }
        ).filter((room) => room !== null)

        const transformedModifyRoomsReservations = {
          reservation: reservationsModifyRoomsRootData,
          rooms: roomsModifyData,
        }

        this.$track(
          this.getBookingTrackingModifyReservation(
            {
              name: 'home >> modify-confirmation',
              hotelCode: this.hotelCode,
              siteSection: 'modify confirmation',
            },
            {}
          )
        )

        this.$track(
          this.getBookingTrackingModifyReservation(
            {
              name: 'home >> modify-confirmation',
              hotelCode: this.hotelCode,
              siteSection: 'modify confirmation',
            },
            {
              event: 'modifyRes',
              reservation: transformedModifyRoomsReservations.reservation,
              rooms: transformedModifyRoomsReservations.rooms,
            }
          )
        )

        this.$track(
          this.getBookingTrackingModifyReservation(
            undefined,
            {
              event: 'bookingStateChange',
              bookingEngine: {
                bookingStepNumber: '4',
                bookingStepName: 'Confirmation',
                bookingEngineVersion: '2',
                bookingFlow: this.isMultiRoomReservation ? 'multiRoomMod' : 'singleRoomMod'
              }
            }
          )
        )
      }
      // SBE-55 Adobe Tracking GuestInfo Event
      if (changedGuests.length > 0) {
        this.$track(
          this.getBookingTrackingModifyReservation(
            {
              name: 'home >> modify-confirmation',
              hotelCode: this.hotelCode,
              siteSection: 'modify confirmation',
            },
            { event: 'modifyGuestInfo' }
          )
        )
      }
      // SBE-71 Adobe Tracking Complimentary Privileges/Enhancements Event
      if (changedEnhancements.length > 0) {
        const reservationsEnhancementsRootData = {
          itineraryID: this.modifyReservationItineraryNumber,
          hotelCode: this.currentReservationsList[0]?.hotelCode || null,
          checkIn: this.startDate,
          checkOut: this.endDate,
          date: formatDateZone(currentDate, null, 'ddd, D MMM, YYYY'),
          roomQty: this.currentReservationsList.length.toString(),
          tax: this.totalCommittedTaxes.toString(),
          currency: this.currencyCode,
          total: modifiedTotal.toString(),
          promoCode: this.currentReservationsList[0]?.roomStays[0]?.promotionCode || '',
        }

        const roomsEnhancementsData = changedEnhancements.map(
          (changedReservation) => {
            const matchedReservation = currentReservationsMap.get(changedReservation.id)

            if (!matchedReservation) {
              return null
            }

            const roomEnhancementPrice = Math.round(
              matchedReservation.roomStays[0]?.total?.amountBeforeTax
            )
            const roomEnhancementTax = Math.round(
              matchedReservation.roomStays[0]?.total?.amountAfterTax
                  - matchedReservation.roomStays[0]?.total?.amountBeforeTax
            )

            const oldMatchingReservation = oldReservationsMap.get(matchedReservation.id)
            const prevEnhancements = (oldMatchingReservation?.enhancementServices || [])
              .map((item) => item.title)
              .join(' > ')

            return {
              confirmationID: matchedReservation.id,
              type: matchedReservation.roomStays[0]?.roomName,
              rate: matchedReservation.ratePlanNameSynXis,
              numNights:
                  matchedReservation.prices.nightlyRates.length.toString(),
              numAdults: matchedReservation.adultsCount.toString(),
              numChildren: matchedReservation.childCount.toString(),
              daysToCheckIn: this.calculateDaysBetweenDates(this.startDate),
              price: roomEnhancementPrice.toString(),
              tax: roomEnhancementTax.toString(),
              enhancements: matchedReservation?.enhancementServices || []
                ? matchedReservation.enhancementServices
                  .map((item) => item.title)
                  .join(' > ')
                : '',
              prevEnhancements
            }
          }
        ).filter((enhancement) => enhancement !== null)

        const transformedEnhancementsReservations = {
          reservation: reservationsEnhancementsRootData,
          rooms: roomsEnhancementsData,
        }

        this.$track(
          this.getBookingTrackingModifyReservation(
            {
              name: 'home >> modify-confirmation',
              hotelCode: this.hotelCode,
              siteSection: 'modify confirmation',
            },
            {}
          )
        )

        this.$track(
          this.getBookingTrackingModifyReservation(
            {
              name: 'home >> modify-confirmation',
              hotelCode: this.hotelCode,
              siteSection: 'modify confirmation',
            },
            {
              event: 'modifyCompPrivileges',
              reservation: transformedEnhancementsReservations.reservation,
              rooms: transformedEnhancementsReservations.rooms,
            }
          )
        )
      }
      return true
    },
  }
}
